import React, { useState, useEffect } from 'react';
import { X, Tag, Mail, Phone, Globe, Instagram, Facebook, CheckCircle, Save } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { SimpleDropdown } from './ui/simple-dropdown';
import { IMaskInput } from 'react-imask';
import { educationAPI } from '../api/axiosClient';

interface BrandCreateFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface BrandFormData {
  name: string;
  email: string;
  phoneCode: string;
  phoneNumber: string;
  webSite: string;
  instagram: string;
  facebook: string;
  isActive: boolean;
}

const countryCodes = [
  { code: '90', flag: '🇹🇷' },
  { code: '1', flag: '🇺🇸' },
  { code: '44', flag: '🇬🇧' },
  { code: '49', flag: '🇩🇪' },
  { code: '33', flag: '🇫🇷' },
  { code: '39', flag: '🇮🇹' },
  { code: '34', flag: '🇪🇸' },
  { code: '31', flag: '🇳🇱' },
  { code: '32', flag: '🇧🇪' },
  { code: '41', flag: '🇨🇭' },
  { code: '43', flag: '🇦🇹' },
  { code: '45', flag: '🇩🇰' },
  { code: '46', flag: '🇸🇪' },
  { code: '47', flag: '🇳🇴' },
  { code: '358', flag: '🇫🇮' },
  { code: '7', flag: '🇷🇺' },
  { code: '86', flag: '🇨🇳' },
  { code: '81', flag: '🇯🇵' },
  { code: '82', flag: '🇰🇷' },
  { code: '91', flag: '🇮🇳' },
  { code: '55', flag: '🇧🇷' },
  { code: '54', flag: '🇦🇷' },
  { code: '52', flag: '🇲🇽' },
  { code: '61', flag: '🇦🇺' },
  { code: '64', flag: '🇳🇿' },
  { code: '27', flag: '🇿🇦' },
  { code: '20', flag: '🇪🇬' },
  { code: '971', flag: '🇦🇪' },
  { code: '966', flag: '🇸🇦' },
  { code: '974', flag: '🇶🇦' },
  { code: '965', flag: '🇰🇼' },
  { code: '973', flag: '🇧🇭' },
  { code: '968', flag: '🇴🇲' },
  { code: '962', flag: '🇯🇴' },
  { code: '961', flag: '🇱🇧' },
  { code: '963', flag: '🇸🇾' },
  { code: '964', flag: '🇮🇶' },
  { code: '98', flag: '🇮🇷' },
  { code: '90', flag: '🇹🇷' }
];

const getPhoneMask = (countryCode: string): string => {
  switch (countryCode) {
    case '90':
      return '000 000 00 00';
    case '1':
      return '(000) 000-0000';
    case '44':
      return '0000 000000';
    case '49':
      return '000 00000000';
    case '33':
      return '0 00 00 00 00';
    case '39':
      return '000 000 0000';
    case '34':
      return '000 00 00 00';
    case '31':
      return '00 0000 0000';
    case '32':
      return '000 00 00 00';
    case '41':
      return '000 000 00 00';
    case '43':
      return '000 0000000';
    case '45':
      return '00 00 00 00';
    case '46':
      return '000-000 00 00';
    case '47':
      return '000 00 000';
    case '358':
      return '000 0000000';
    case '7':
      return '000 000-00-00';
    case '86':
      return '000 0000 0000';
    case '81':
      return '000-0000-0000';
    case '82':
      return '000-0000-0000';
    case '91':
      return '00000 00000';
    case '55':
      return '(00) 00000-0000';
    case '54':
      return '000 000-0000';
    case '52':
      return '000 000 0000';
    case '61':
      return '000 000 000';
    case '64':
      return '000 000 0000';
    case '27':
      return '000 000 0000';
    case '20':
      return '000 0000 0000';
    case '971':
      return '000 000 0000';
    case '966':
      return '000 000 0000';
    case '974':
      return '0000 0000';
    case '965':
      return '0000 0000';
    case '973':
      return '0000 0000';
    case '968':
      return '0000 0000';
    case '962':
      return '00 0000 0000';
    case '961':
      return '00 000 000';
    case '963':
      return '000 000 0000';
    case '964':
      return '000 000 0000';
    case '98':
      return '000 000 0000';
    default:
      return '000 000 0000';
  }
};

const getPhonePlaceholder = (countryCode: string): string => {
  switch (countryCode) {
    case '90':
      return '555 123 45 67';
    case '1':
      return '(555) 123-4567';
    case '44':
      return '7700 900123';
    case '49':
      return '30 12345678';
    case '33':
      return '1 23 45 67 89';
    case '39':
      return '312 345 6789';
    case '34':
      return '612 34 56 78';
    case '31':
      return '6 12345678';
    case '32':
      return '470 12 34 56';
    case '41':
      return '79 123 45 67';
    case '43':
      return '664 1234567';
    case '45':
      return '12 34 56 78';
    case '46':
      return '70-123 45 67';
    case '47':
      return '412 34 567';
    case '358':
      return '50 1234567';
    case '7':
      return '912 345-67-89';
    case '86':
      return '138 0013 8000';
    case '81':
      return '90-1234-5678';
    case '82':
      return '10-1234-5678';
    case '91':
      return '98765 43210';
    case '55':
      return '(11) 99999-9999';
    case '54':
      return '11 1234-5678';
    case '52':
      return '55 1234 5678';
    case '61':
      return '412 345 678';
    case '64':
      return '21 123 4567';
    case '27':
      return '82 123 4567';
    case '20':
      return '10 1234 5678';
    case '971':
      return '50 123 4567';
    case '966':
      return '50 123 4567';
    case '974':
      return '1234 5678';
    case '965':
      return '1234 5678';
    case '973':
      return '1234 5678';
    case '968':
      return '1234 5678';
    case '962':
      return '77 0000 0000';
    case '961':
      return '71 000 000';
    case '963':
      return '944 123 456';
    case '964':
      return '770 123 4567';
    case '98':
      return '912 123 4567';
    default:
      return '555 123 4567';
  }
};

const BrandCreateForm: React.FC<BrandCreateFormProps> = ({ isOpen, onClose, onSuccess }) => {
  const [brandForm, setBrandForm] = useState<BrandFormData>({
    name: '',
    email: '',
    phoneCode: '90',
    phoneNumber: '',
    webSite: '',
    instagram: '',
    facebook: '',
    isActive: true
  });

  const [selectedCountryCode, setSelectedCountryCode] = useState<{ id: string | number; label: string; value: string | number } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      // Reset form when opening
      setBrandForm({
        name: '',
        email: '',
        phoneCode: '90',
        phoneNumber: '',
        webSite: '',
        instagram: '',
        facebook: '',
        isActive: true
      });
      setSelectedCountryCode(countryCodes.find(code => code.code === '90') ? {
        id: '90',
        label: '🇹🇷 +90',
        value: '90'
      } : null);
      setFormErrors({});
    }
  }, [isOpen]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setBrandForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!brandForm.name.trim()) {
      errors.name = 'Marka adı gereklidir';
    }

    if (!brandForm.email.trim()) {
      errors.email = 'E-posta gereklidir';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(brandForm.email)) {
      errors.email = 'Geçerli bir e-posta adresi giriniz';
    }

    if (!brandForm.phoneNumber.trim()) {
      errors.phoneNumber = 'Telefon numarası gereklidir';
    }

    if (brandForm.webSite && !/^https?:\/\/.+/.test(brandForm.webSite)) {
      errors.webSite = 'Geçerli bir website adresi giriniz (http:// veya https:// ile başlamalı)';
    }

    if (brandForm.instagram && !brandForm.instagram.startsWith('@')) {
      errors.instagram = 'Instagram kullanıcı adı @ ile başlamalıdır';
    }

    if (brandForm.facebook && !brandForm.facebook.includes('facebook.com/')) {
      errors.facebook = 'Facebook sayfa adı facebook.com/ içermelidir';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await educationAPI.createSchoolBrand(brandForm);
      
      if (response.success) {
        onSuccess();
        onClose();
      } else {
        console.error('Marka oluşturma hatası:', response.message);
        alert('Marka oluşturulurken bir hata oluştu: ' + response.message);
      }
    } catch (error) {
      console.error('Marka oluşturma hatası:', error);
      alert('Marka oluşturulurken bir hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Yeni Marka Ekle
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Marka Adı */}
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-gray-700 flex items-center mb-2">
                <Tag className="w-4 h-4 mr-2" />
                Marka Adı *
              </Label>
              <Input
                id="name"
                type="text"
                value={brandForm.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Marka adını girin"
                className={`w-full rounded-md border bg-white px-3 py-2 text-sm outline-none ring-2 ring-transparent transition focus:border-gray-400 focus:ring-gray-200 disabled:cursor-not-allowed disabled:bg-gray-100 hover:border-gray-400 ${
                  formErrors.name ? 'border-red-500 focus:ring-red-200' : 'border-gray-300'
                }`}
                disabled={isSubmitting}
              />
              {formErrors.name && (
                <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
              )}
            </div>

            {/* E-posta */}
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-gray-700 flex items-center mb-2">
                <Mail className="w-4 h-4 mr-2" />
                E-posta *
              </Label>
              <Input
                id="email"
                type="text"
                value={brandForm.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="ornek@email.com"
                className={`w-full rounded-md border bg-white px-3 py-2 text-sm outline-none ring-2 ring-transparent transition focus:border-gray-400 focus:ring-gray-200 disabled:cursor-not-allowed disabled:bg-gray-100 hover:border-gray-400 ${
                  formErrors.email ? 'border-red-500 focus:ring-red-200' : 'border-gray-300'
                }`}
                disabled={isSubmitting}
              />
              {formErrors.email && (
                <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
              )}
            </div>

            {/* Telefon Numarası */}
            <div>
              <Label htmlFor="phoneNumber" className="text-sm font-medium text-gray-700 flex items-center mb-2">
                <Phone className="w-4 h-4 mr-2" />
                Telefon Numarası *
              </Label>
              <div className="flex space-x-2">
                <SimpleDropdown
                  options={countryCodes.map((code) => ({
                    id: code.code,
                    label: `${code.flag} +${code.code}`,
                    value: code.code
                  }))}
                  value={selectedCountryCode}
                  onChange={(countryCode) => {
                    if (countryCode) {
                      setSelectedCountryCode(countryCode);
                      handleInputChange('phoneCode', String(countryCode.value));
                      // Reset phone number when country changes
                      handleInputChange('phoneNumber', '');
                    }
                  }}
                  placeholder="Ülke seçiniz"
                  className="w-24"
                  disabled={isSubmitting}
                />
                <IMaskInput
                  mask={getPhoneMask(brandForm.phoneCode)}
                  value={brandForm.phoneNumber}
                  onAccept={(value: string) => handleInputChange('phoneNumber', value)}
                  placeholder={getPhonePlaceholder(brandForm.phoneCode)}
                  className={`flex-1 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 transition hover:border-gray-400 ${
                    formErrors.phoneNumber ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-gray-200'
                  }`}
                  inputMode="numeric"
                  disabled={isSubmitting}
                />
              </div>
              {formErrors.phoneNumber && (
                <p className="text-red-500 text-xs mt-1">{formErrors.phoneNumber}</p>
              )}
            </div>

            {/* Website */}
            <div>
              <Label htmlFor="webSite" className="text-sm font-medium text-gray-700 flex items-center mb-2">
                <Globe className="w-4 h-4 mr-2" />
                Website
              </Label>
              <Input
                id="webSite"
                type="url"
                value={brandForm.webSite}
                onChange={(e) => handleInputChange('webSite', e.target.value)}
                placeholder="https://www.example.com"
                className={`w-full rounded-md border bg-white px-3 py-2 text-sm outline-none ring-2 ring-transparent transition focus:border-gray-400 focus:ring-gray-200 disabled:cursor-not-allowed disabled:bg-gray-100 hover:border-gray-400 ${
                  formErrors.webSite ? 'border-red-500 focus:ring-red-200' : 'border-gray-300'
                }`}
                disabled={isSubmitting}
              />
              {formErrors.webSite && (
                <p className="text-red-500 text-xs mt-1">{formErrors.webSite}</p>
              )}
            </div>

            {/* Instagram */}
            <div>
              <Label htmlFor="instagram" className="text-sm font-medium text-gray-700 flex items-center mb-2">
                <Instagram className="w-4 h-4 mr-2" />
                Instagram
              </Label>
              <Input
                id="instagram"
                type="text"
                value={brandForm.instagram}
                onChange={(e) => handleInputChange('instagram', e.target.value)}
                placeholder="@kullaniciadi"
                className={`w-full rounded-md border bg-white px-3 py-2 text-sm outline-none ring-2 ring-transparent transition focus:border-gray-400 focus:ring-gray-200 disabled:cursor-not-allowed disabled:bg-gray-100 hover:border-gray-400 ${
                  formErrors.instagram ? 'border-red-500 focus:ring-red-200' : 'border-gray-300'
                }`}
                disabled={isSubmitting}
              />
              {formErrors.instagram && (
                <p className="text-red-500 text-xs mt-1">{formErrors.instagram}</p>
              )}
            </div>

            {/* Facebook */}
            <div>
              <Label htmlFor="facebook" className="text-sm font-medium text-gray-700 flex items-center mb-2">
                <Facebook className="w-4 h-4 mr-2" />
                Facebook
              </Label>
              <Input
                id="facebook"
                type="text"
                value={brandForm.facebook}
                onChange={(e) => handleInputChange('facebook', e.target.value)}
                placeholder="facebook.com/sayfaadi"
                className={`w-full rounded-md border bg-white px-3 py-2 text-sm outline-none ring-2 ring-transparent transition focus:border-gray-400 focus:ring-gray-200 disabled:cursor-not-allowed disabled:bg-gray-100 hover:border-gray-400 ${
                  formErrors.facebook ? 'border-red-500 focus:ring-red-200' : 'border-gray-300'
                }`}
                disabled={isSubmitting}
              />
              {formErrors.facebook && (
                <p className="text-red-500 text-xs mt-1">{formErrors.facebook}</p>
              )}
            </div>
          </div>

          {/* Durum - Full width */}
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-2 block flex items-center">
              <CheckCircle className="w-4 h-4 mr-2" />
              Durum
            </Label>
            <RadioGroup 
              value={brandForm.isActive ? "active" : "inactive"}
              onValueChange={(value: string) => handleInputChange('isActive', value === "active")}
              className="flex items-center space-x-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="active" id="active" className="text-[#9B51E0] border-gray-300 focus:ring-[#9B51E0] focus:ring-offset-2" />
                <Label htmlFor="active" className="text-sm text-gray-700">
                  Aktif
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="inactive" id="inactive" className="text-[#9B51E0] border-gray-300 focus:ring-[#9B51E0] focus:ring-offset-2" />
                <Label htmlFor="inactive" className="text-sm text-gray-700">
                  Pasif
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              İptal
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-[#9B51E0] hover:bg-[#8B41D0]"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Ekleniyor...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Kaydet
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BrandCreateForm;
